import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Box, inlineMediaQueryMixin } from "@zillow/constellation";
import { forwardRef, useCallback } from "react";
import styled from "styled-components";
import Source from "../Source/Source.esm.js";
import Video from "../Video/Video.esm.js";
const BaseBackgroundStyledVideo = styled(Video).withConfig({
  displayName: "GroupBackgroundVideo__BaseBackgroundStyledVideo",
  componentId: "sc-2vrk1d-0"
})(["&&&{pointer-events:none;position:absolute;top:0;right:0;bottom:0;left:0;", " width:100%;height:100%;", "}"], inlineMediaQueryMixin("size", (mediaProps) => `object-fit: ${mediaProps.size};`), inlineMediaQueryMixin("position", (mediaProps) => `object-position: ${mediaProps.position};`));
const MobileViewportVideo = forwardRef((props, ref) => /* @__PURE__ */ jsx(Box, { ...props, ref, as: BaseBackgroundStyledVideo, className: `exclude-from-algorithmic-darkening ${props.className || ""}`, sx: {
  "&&": {
    display: props.hideOnMobile ? "none" : "block",
    "@media $md": {
      display: "none"
    }
  }
} }));
MobileViewportVideo.displayName = "MobileViewportVideo";
const TabletViewportVideo = forwardRef((props, ref) => /* @__PURE__ */ jsx(Box, { ...props, ref, as: BaseBackgroundStyledVideo, className: `exclude-from-algorithmic-darkening ${props.className || ""}`, sx: {
  "&&": {
    display: props.hideOnMobile ? "none" : "block",
    "@media $md": {
      display: props.hideOnTablet ? "none" : "block"
    },
    "@media $lg": {
      display: props.hideOnTablet ? "none" : "block"
    },
    "@media $xl": {
      display: "none"
    }
  }
} }));
TabletViewportVideo.displayName = "TabletViewportVideo";
const DesktopViewportVideo = forwardRef((props, ref) => /* @__PURE__ */ jsx(Box, { ...props, ref, as: BaseBackgroundStyledVideo, className: `exclude-from-algorithmic-darkening ${props.className || ""}`, sx: {
  "&&": {
    display: props.hideOnMobile ? "none" : "block",
    "@media $md": {
      display: props.hideOnTablet ? "none" : "block"
    },
    "@media $xl": {
      display: props.hideOnDesktop ? "none" : "block"
    }
  }
} }));
DesktopViewportVideo.displayName = "DesktopViewportVideo";
const GroupBackgroundVideo = ({
  position = {
    default: "50% 50%"
  },
  size = {
    default: "cover"
  },
  source,
  visible = {
    default: true,
    lg_lte: true,
    sm_lte: true
  }
}) => {
  var _a, _b, _c;
  const muteVideoOnMountRef = useMuteVideoOnMountRef();
  const desktopSource = ((_a = source == null ? void 0 : source.default) == null ? void 0 : _a.src) ? source.default : void 0;
  const tabletSource = ((_b = source == null ? void 0 : source.lg_lte) == null ? void 0 : _b.src) ? source.lg_lte : void 0;
  const mobileSource = ((_c = source == null ? void 0 : source.sm_lte) == null ? void 0 : _c.src) ? source.sm_lte : void 0;
  const hasBackgroundVideo = desktopSource || tabletSource || mobileSource;
  if (!hasBackgroundVideo) {
    return null;
  }
  const commonVideoProps = {
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    size,
    position
  };
  const hasMobileVideo = !!mobileSource;
  const hasTabletVideo = !!tabletSource;
  const hasDesktopVideo = !!desktopSource;
  const isVisibleOnDesktop = !!(visible == null ? void 0 : visible.default);
  const isVisibleOnTablet = !!(visible == null ? void 0 : visible.lg_lte);
  const isVisibleOnMobile = !!(visible == null ? void 0 : visible.sm_lte);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    hasMobileVideo && /* @__PURE__ */ jsx(MobileViewportVideo, { ...commonVideoProps, hideOnMobile: !isVisibleOnMobile, className: "mobile-video", ref: muteVideoOnMountRef, children: /* @__PURE__ */ jsx(Source, { src: mobileSource.src }) }),
    hasTabletVideo && /* @__PURE__ */ jsx(
      TabletViewportVideo,
      {
        hideOnMobile: !isVisibleOnMobile || hasMobileVideo,
        hideOnTablet: !isVisibleOnTablet,
        ...commonVideoProps,
        className: "tablet-video",
        ref: muteVideoOnMountRef,
        children: /* @__PURE__ */ jsx(Source, { src: tabletSource.src })
      }
    ),
    hasDesktopVideo && /* @__PURE__ */ jsx(
      DesktopViewportVideo,
      {
        ...commonVideoProps,
        className: "desktop-video",
        hideOnMobile: !isVisibleOnMobile || hasMobileVideo || hasTabletVideo,
        hideOnTablet: !isVisibleOnTablet || hasTabletVideo,
        hideOnDesktop: !isVisibleOnDesktop,
        ref: muteVideoOnMountRef,
        children: /* @__PURE__ */ jsx(Source, { src: desktopSource.src })
      }
    )
  ] });
};
function useMuteVideoOnMountRef() {
  return useCallback(function useMuteVideoOnMountRef2(ref) {
    if (!ref) {
      return;
    }
    ref.setAttribute("muted", "");
  }, []);
}
export {
  GroupBackgroundVideo as default
};
